import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  DomainContext,
  getSocketStatusConfig,
  useSocketStatus,
  useTranslation,
} from "hhn-commons";
import { useContext } from "react";

import {
  BigCloudOff,
  Container,
  NoSocketConnectionTitle,
  Overlay,
  TapToConnect,
} from "./SocketConnectionErrorHandler.style";

interface SocketConnectionErrorHandlerProps {
  children: React.ReactNode;
}

export function SocketConnectionErrorHandler({
  children,
}: SocketConnectionErrorHandlerProps) {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "networkInfo" });
  const { domain } = useContext(DomainContext);
  const { user } = domain;
  const socketManager = user?.getSocketManager();
  const allSocketsConnected = socketManager?.allSocketsConnected();
  const areSocketsConnecting = socketManager?.areSocketsConnecting();
  const { perOrgStatus } = useSocketStatus();

  return (
    <>
      {!allSocketsConnected && (
        <Overlay style={{ zIndex: 1400 }}>
          <Container>
            <BigCloudOff />
            <NoSocketConnectionTitle>
              {tNs("noSocketConnection")}
            </NoSocketConnectionTitle>
            <TapToConnect>{tNs("tapToConnect")}</TapToConnect>
            <List sx={{ width: "100%", maxWidth: 400, mb: 2 }}>
              {perOrgStatus.map(org => {
                const config = getSocketStatusConfig(
                  org.isConnected,
                  org.isConnecting,
                  org.retryCount,
                );
                return (
                  <ListItem key={org.organizationId}>
                    <ListItemIcon>
                      {org.isConnected ? (
                        <CloudDoneIcon sx={{ color: config.color }} />
                      ) : org.isConnecting ? (
                        <SyncIcon sx={{ color: config.color }} />
                      ) : (
                        <CloudOffIcon sx={{ color: config.color }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={org.organizationName}
                      secondary={config.text}
                      slotProps={{
                        secondary: { sx: { color: config.color } }
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
            <Button
              variant="contained"
              disabled={areSocketsConnecting}
              color="primary"
              data-testid="socket-connect-btn"
              onClick={async () => {
                window.location.reload();
              }}
            >
              {areSocketsConnecting ? tNs("connecting") : tNs("connect")}
            </Button>
          </Container>
        </Overlay>
      )}
      {children}
    </>
  );
}
